/**
 * FeedbackAttachmentThumb — 反馈用户附件预览缩略（ADM-FBK-S001）
 *
 * 附件经鉴权代理端点读取（GET /api/v1/feedback/:fid/attachments/:id，需 admin JWT），故**必须**走
 * authedFetch（带 Bearer + 401 自动续期），不能裸 <img src>（裸 img 不带 Authorization → 401）。
 * authedFetch → blob → object URL → <img>；卸载时 revoke。PII 提示：CONTAINS_FACE/DOCUMENT 标红警示。
 */

import type { FeedbackAttachmentItem } from '@novel-admin/shared';
import { useState } from 'react';
import { useAuthedImage } from '~/hooks/use-authed-image';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';

/** 字节 → 紧凑 KB/MB 展示（附件 ≤10MB）。 */
function fmtSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * PII 分级 → 中文警示标签（运营预览前知情）。SCREENSHOT_GENERIC/UNKNOWN 不警示。
 * 注（W-2 P3②）：MVP 期 pii_class 恒 UNKNOWN（dev-docs 90-反馈 §1.5，未接 ML scan）→ 本 chip 当前不显示，
 * 为未来 ML scan 分类预留（forward-compat，非 dead UI）。
 */
const PII_WARN: Record<string, string> = {
  CONTAINS_FACE: '含人脸',
  CONTAINS_DOCUMENT: '含证件',
};

export function FeedbackAttachmentThumb({ att }: { att: FeedbackAttachmentItem }) {
  // 鉴权代理取图（authedFetch → blob → objectURL，卸载/url 变更自动 revoke）——公用 hook
  const { objUrl, failed } = useAuthedImage(att.url);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewScale, setPreviewScale] = useState(1);
  const isImage = att.content_type.startsWith('image/');
  const piiWarn = PII_WARN[att.pii_class];

  return (
    <div
      className="card-flat"
      style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6, width: 132 }}
    >
      <div
        style={{
          width: 116,
          height: 88,
          borderRadius: 6,
          overflow: 'hidden',
          background: 'var(--bg-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {failed ? (
          <span style={{ fontSize: 11, color: 'var(--neg)' }}>加载失败</span>
        ) : isImage && objUrl ? (
          // 鉴权代理流的 blob object URL（非 object_key、非公开签名 URL）
          <button
            type="button"
            className="btn btn-ghost"
            aria-label="放大反馈附件"
            style={{ width: '100%', height: '100%', padding: 0, cursor: 'zoom-in' }}
            onClick={() => {
              setPreviewScale(1);
              setPreviewOpen(true);
            }}
          >
            <img
              src={objUrl}
              alt="反馈附件"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          </button>
        ) : objUrl ? (
          <a href={objUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">
            <Icons.IconDownload size={12} /> 打开附件
          </a>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>加载中…</span>
        )}
      </div>
      {previewOpen && objUrl && (
        <Dialog
          title="反馈附件预览"
          width={1000}
          onClose={() => setPreviewOpen(false)}
          footer={
            <>
              <button
                className="btn btn-sm"
                disabled={previewScale <= 0.5}
                onClick={() => setPreviewScale((scale) => Math.max(0.5, scale - 0.5))}
              >
                缩小
              </button>
              <button
                className="btn btn-sm mono"
                onClick={() => setPreviewScale(1)}
                aria-label="重置缩放"
              >
                {Math.round(previewScale * 100)}%
              </button>
              <button
                className="btn btn-sm"
                disabled={previewScale >= 3}
                onClick={() => setPreviewScale((scale) => Math.min(3, scale + 0.5))}
              >
                放大
              </button>
              {piiWarn && <span className="chip chip-warn">{piiWarn}</span>}
            </>
          }
        >
          <div style={{ overflow: 'auto', maxHeight: '75vh' }}>
            <img
              src={objUrl}
              alt="反馈附件大图"
              style={{
                display: 'block',
                width: `${previewScale * 100}%`,
                maxWidth: 'none',
                maxHeight: `${previewScale * 70}vh`,
                objectFit: 'contain',
                margin: '0 auto',
              }}
            />
          </div>
        </Dialog>
      )}
      <div className="hstack" style={{ gap: 6, fontSize: 10.5, color: 'var(--fg-muted)' }}>
        <span className="mono">{fmtSize(att.size_bytes)}</span>
        {piiWarn && (
          <span className="chip chip-warn" style={{ fontSize: 9.5 }}>
            {piiWarn}
          </span>
        )}
      </div>
    </div>
  );
}
